// src/components/RadioPlayer.js
import React, { useEffect, useRef } from 'react';
import {
  FaMusic,
  FaPause,
  FaPlay,
  FaStepBackward,
  FaStepForward,
  FaVolumeMute,
  FaVolumeUp,
} from 'react-icons/fa';
import { useRadio } from '../RadioContext';

const RadioPlayer = () => {
  const {
    currentSong,
    currentSongIndex,
    isPlaying,
    volume,
    isMuted,
    pause,
    goToNextSong,
  } = useRadio();
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = isMuted ? 0 : volume;
  }, [isMuted, volume]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.load();

    if (isPlaying) {
      audio.play().catch(() => {
        pause();
      });
    }
  }, [currentSongIndex]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.play().catch(() => {
        pause();
      });
    } else {
      audio.pause();
    }
  }, [isPlaying, pause]);

  return (
    <audio
      ref={audioRef}
      src={currentSong.src}
      loop={false}
      onEnded={goToNextSong}
    />
  );
};

export function RadioSettingsPanel() {
  const {
    songs,
    currentSong,
    currentSongIndex,
    userLevel,
    tracksLoading,
    tracksError,
    isUsingFallbackTracks,
    isPlaying,
    volume,
    isMuted,
    togglePlay,
    goToPreviousSong,
    goToNextSong,
    selectSong,
    toggleMute,
    updateVolume,
  } = useRadio();

  return (
    <div className="pfp-profile-radio">
      <div className="pfp-profile-radio__now">
        {currentSong.coverUrl ? (
          <img className="pfp-profile-radio__cover" src={currentSong.coverUrl} alt="" />
        ) : (
          <span className="pfp-profile-radio__icon" aria-hidden="true">
            <FaMusic />
          </span>
        )}
        <div>
          <span>Hintergrundmusik</span>
          <strong>{currentSong.title}</strong>
          <small>Dein Level: {userLevel}</small>
        </div>
      </div>

      {(tracksLoading || tracksError || isUsingFallbackTracks) && (
        <div className={`pfp-profile-radio__status ${tracksError ? 'is-error' : ''}`}>
          {tracksLoading
            ? 'Radio Tracks werden aus dem WordPress-Backend geladen ...'
            : tracksError
              ? 'Backend-Tracks konnten nicht geladen werden. Es läuft die feste Fallback-Liste.'
              : 'Es läuft die feste Fallback-Liste, weil im Backend noch keine nutzbaren Tracks gefunden wurden.'}
        </div>
      )}

      <div className="pfp-profile-radio__controls" aria-label="Musiksteuerung">
        <button type="button" onClick={goToPreviousSong} aria-label="Vorheriger Titel">
          <FaStepBackward />
        </button>
        <button type="button" className="pfp-profile-radio__play" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Abspielen'}>
          {isPlaying ? <FaPause /> : <FaPlay />}
        </button>
        <button type="button" onClick={goToNextSong} aria-label="Nächster Titel">
          <FaStepForward />
        </button>
      </div>

      <div className="pfp-profile-radio__volume">
        <button type="button" onClick={toggleMute} aria-label={isMuted ? 'Ton einschalten' : 'Ton stummschalten'}>
          {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={isMuted ? 0 : volume}
          onChange={(event) => updateVolume(Number(event.target.value))}
          aria-label="Lautstärke"
        />
      </div>

      <ol className="pfp-profile-radio__track-list" aria-label="Verfügbare Musiktitel">
        {songs.map((song, index) => {
          const isActive = index === currentSongIndex;
          const isLocked = !song.isUnlocked;

          return (
            <li key={song.src}>
              <button
                type="button"
                className={`${isActive ? 'is-active' : ''} ${isLocked ? 'is-locked' : ''}`}
                onClick={() => selectSong(index)}
                disabled={isLocked}
                aria-current={isActive ? 'true' : undefined}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                {song.coverUrl ? (
                  <img className="pfp-profile-radio__track-cover" src={song.coverUrl} alt="" />
                ) : (
                  <span className="pfp-profile-radio__track-cover pfp-profile-radio__track-cover--placeholder" aria-hidden="true">
                    <FaMusic />
                  </span>
                )}
                <strong>{song.title}</strong>
                {isLocked ? (
                  <em>ab Level {song.minLevel}</em>
                ) : (
                  isActive && <em>{isPlaying ? 'läuft' : 'ausgewählt'}</em>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default RadioPlayer;
